import { DCEvent } from "@/events/eventsDomain";
import { isTMessage, TMessage } from "@/i18n/tt";
import { useDispatch, useTranslation } from "@/stores/RootStoreContext";
import { classNames } from "@/utils/classNames";
import { preventDefault } from "@/utils/events";
import { isArrowLeft, isArrowRight } from "@/utils/keyboardEvents";
import { debounce } from "lodash";
import {
  FocusEvent,
  FocusEventHandler,
  KeyboardEvent,
  MouseEvent,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box } from "./Box";
import { BoxProps } from "./boxHelpers";
import { Icon } from "./Icon";
import styles from "./Tabs.module.css";
import { Tag, TagProps } from "./Tag";

type ScrollState = {
  scrollLeft: number;
  scrollWidth: number;
  viewportWidth: number;
};

function isOverflowLeft({ scrollLeft }: ScrollState): boolean {
  return Math.floor(scrollLeft) > 0;
}

function isOverflowRight({ scrollLeft, scrollWidth, viewportWidth }: ScrollState): boolean {
  return Math.round(scrollWidth) - Math.round(scrollLeft) > viewportWidth;
}

function ScrollButton({
  dir,
  disabled,
  onClick,
}: {
  dir: "left" | "right";
  disabled: boolean;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      tabIndex={-1}
      aria-hidden
      className={classNames(
        "absolute",
        dir === "left" ? "left-0" : "right-0",
        "h-full",
        "text-sm",
        "p-2",
        "font-bold",
        disabled && "invisible",
      )}
      style={{
        backgroundImage: `linear-gradient(to ${dir}, transparent, var(--color-bg-default) 50%)`,
      }}
    >
      {dir === "left" ? (
        <Icon name={"caret-left"} weight={"bold"} />
      ) : (
        <Icon name={"caret-right"} weight={"bold"} />
      )}
    </button>
  );
}

export type TabElementProps = {
  className: string;
  role: "tab";
  "aria-selected": boolean;
  tabIndex: 0 | -1;
  onFocus: FocusEventHandler<HTMLElement>;
};

function Tab({
  onSelect,
  isActive,
  isTabbable,
  onFocus,
  disabled,
  ...props
}: TabProps & {
  isTabbable: boolean;
  onFocus: (event: FocusEvent<HTMLElement>) => void;
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const className = classNames(
    "text-sm",
    "py-3",
    "px-6",
    "border-b-2",
    isActive ? "border-gray-x-hard" : ["border-transparent", "subtle"],
    disabled && "opacity-50",
    "whitespace-nowrap",
    !disabled && "hover:bg-gray",
  );
  const elementProps: TabElementProps = {
    className,
    role: "tab",
    "aria-selected": Boolean(isActive),
    tabIndex: isTabbable ? 0 : -1,
    onFocus,
  };
  if ("render" in props) {
    return props.render(elementProps);
  }
  return (
    <button
      {...elementProps}
      type={"button"}
      disabled={disabled}
      onClick={preventDefault(() => {
        if (Array.isArray(onSelect)) {
          dispatch(onSelect);
        } else {
          onSelect();
        }
      })}
    >
      <Box>
        <span>{isTMessage(props.label) ? t(props.label) : props.label}</span>
        {Boolean(props.tag) &&
          (typeof props.tag === "number" || typeof props.tag === "string" ? (
            <Tag color={"success"} label={String(props.tag)} />
          ) : typeof props.tag === "object" ? (
            <Tag {...props.tag} />
          ) : undefined)}
      </Box>
    </button>
  );
}

export type TabButtonProps = {
  id: string;
  label: ReactNode | string | TMessage;
  isActive?: boolean;
  tag?: string | number | TagProps;
  onSelect: (() => void) | DCEvent[];
  disabled?: boolean;
};

export type CustomTabProps = {
  id: string;
  render: (props: TabElementProps) => ReactElement;
  isActive?: boolean;
  onSelect: (() => void) | DCEvent[];
  disabled?: boolean;
};

export type TabProps = TabButtonProps | CustomTabProps;

export type TabsProps = {
  tabs: TabProps[];
  scrollable?: boolean;
} & BoxProps;

const tabbableTabId = (tabs: TabProps[]): string | undefined => {
  const enabled = tabs.filter((t) => !t.disabled);
  return (enabled.find((t) => t.isActive) ?? enabled[0])?.id;
};

const focusableTabs = (container: HTMLElement): HTMLElement[] =>
  Array.from(container.querySelectorAll<HTMLElement>('[role="tab"]')).filter(
    (el) => !el.matches(":disabled") && el.getAttribute("aria-disabled") !== "true",
  );

const nextTabIndex = (event: KeyboardEvent, current: number, count: number): number | undefined => {
  if (isArrowRight(event)) return (current + 1) % count;
  if (isArrowLeft(event)) return (current - 1 + count) % count;
  if (event.key === "Home") return 0;
  if (event.key === "End") return count - 1;
  return undefined;
};

export function Tabs({ tabs, scrollable = true, ...props }: TabsProps) {
  const tabsKey = tabs.map((t) => t.id).join();
  const mounted = useRef(false);
  const [ref, setRef] = useState<HTMLDivElement | null>(null);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(0);
  const tabbableId = tabbableTabId(tabs);

  const updateScrollState = useCallback(() => {
    if (mounted.current && ref) {
      setScrollLeft(ref.scrollLeft);
      setScrollWidth(ref.scrollWidth);
      setViewportWidth(ref.clientWidth);
    }
  }, [ref]);

  const debouncedUpdateScrollState = useMemo(
    () => debounce(updateScrollState, 125),
    [updateScrollState],
  );

  const handleScroll = useCallback(() => {
    debouncedUpdateScrollState();
  }, [debouncedUpdateScrollState]);

  const handleFocus = useCallback((event: FocusEvent<HTMLElement>) => {
    event.target.scrollIntoView({ inline: "nearest", block: "nearest", behavior: "smooth" });
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (!ref || !(event.target instanceof HTMLElement)) return;
      const focusable = focusableTabs(ref);
      const current = focusable.indexOf(event.target);
      if (current < 0) return;
      const next = nextTabIndex(event, current, focusable.length);
      if (next === undefined) return;
      event.preventDefault();
      focusable[next].focus();
    },
    [ref],
  );

  const handleScrollLeftClick = useCallback(() => {
    if (ref) {
      ref.scrollBy({ left: -viewportWidth, behavior: "smooth" });
    }
  }, [viewportWidth, ref]);

  const handleScrollRightClick = useCallback(() => {
    if (ref) {
      ref.scrollBy({ left: viewportWidth, behavior: "smooth" });
    }
  }, [viewportWidth, ref]);

  useEffect(() => {
    if (ref) {
      const ro = new ResizeObserver(() => {
        debouncedUpdateScrollState();
      });
      ro.observe(ref);
      return () => {
        ro.disconnect();
      };
    }
  }, [ref, debouncedUpdateScrollState]);

  useEffect(() => {
    debouncedUpdateScrollState();
  }, [tabsKey, debouncedUpdateScrollState]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  return (
    <Box className={"relative"} gap={false} {...props}>
      {scrollable && (
        <ScrollButton
          disabled={!isOverflowLeft({ scrollLeft, scrollWidth, viewportWidth })}
          dir={"left"}
          onClick={handleScrollLeftClick}
        />
      )}
      <div
        ref={setRef}
        role={"tablist"}
        className={classNames(
          styles.tabsScrollContainer,
          "flex",
          "grow",
          scrollable && "overflow-x-scroll",
        )}
        onScroll={handleScroll}
        onKeyDown={handleKeyDown}
      >
        {tabs.map((t) => (
          <Tab {...t} key={t.id} isTabbable={t.id === tabbableId} onFocus={handleFocus} />
        ))}
      </div>
      {scrollable && (
        <ScrollButton
          disabled={!isOverflowRight({ scrollLeft, scrollWidth, viewportWidth })}
          dir={"right"}
          onClick={handleScrollRightClick}
        />
      )}
    </Box>
  );
}
